<!--
	File-upload control for an "Upload crash data" legend section.
	Supports ZIP (DBF shapefile via Numetric), XLSX (Diexsys), and CSV (Tribal Crash Reporting Tool).

	When no data is loaded: shows a file input.
	While parsing: shows a loading indicator.
	When data is loaded: shows legend rows (toggle per severity), crash count, and a Remove button.

	The host app passes the `systemId` of the legend layer that backs the
	uploaded-crashes display. The component looks that legend entry up in
	`legendStore` and binds its visibility toggles to it.
-->
<script lang="ts">
	import { PATCH_FALLBACK } from '#lib/vizPalette.js';
	import type { Feature, FeatureCollection, GeoJsonProperties, Point } from 'geojson';
	import type { DataTable } from '@safesystem/zip-dbf-reader-lib';

	import { customCrashesStore } from '#lib/stores/customCrashesStore.svelte.js';
	import { legendStore } from '#lib/stores/legendStore.svelte.js';
	import { featureSelectionStore } from '#lib/stores/featureSelectionStore.svelte.js';
	import LegendPatch from '#lib/components/LegendPatch.svelte';
	import LegendRow from '#lib/components/LegendRow.svelte';
	import AbleyButton from '#lib/components/AbleyButton.svelte';
	import LoadingSpinner from '#lib/components/LoadingSpinner.svelte';

	import { getLogger } from '@logtape/logtape';

	const log = getLogger(['platform', 'custom-crashes']);

	const { systemId }: { systemId: string } = $props();

	const crashLegend = $derived(legendStore.layers.find((l) => l.systemId === systemId));

	let loading = $state(false);

	// Cap uploads at 50 MB. Files are parsed entirely client-side; without a
	// limit a multi-GB file locks the user's tab. Self-DoS only.
	const MAX_FILE_BYTES = 50 * 1024 * 1024;

	/**
	 * Coerce arbitrary cell values to a longitude/latitude pair, returning
	 * `undefined` when either coordinate is non-numeric or out of range. The
	 * uploaded data is untrusted — without this check, NaN / out-of-range values
	 * silently produce garbage MapLibre features.
	 */
	const toValidCoords = (lon: unknown, lat: unknown): [number, number] | undefined => {
		const lonNum = Number(lon);
		const latNum = Number(lat);
		if (!Number.isFinite(lonNum) || !Number.isFinite(latNum)) return undefined;
		if (Math.abs(latNum) > 90 || Math.abs(lonNum) > 180) return undefined;
		return [lonNum, latNum];
	};

	const pointFeature = (
		coordinates: [number, number],
		properties: GeoJsonProperties
	): Feature<Point> => ({ type: 'Feature', properties, geometry: { type: 'Point', coordinates } });

	const toCollection = (features: (Feature | undefined)[]): FeatureCollection => ({
		type: 'FeatureCollection',
		features: features.filter((f) => f !== undefined)
	});

	// ---- GeoJSON parsers ----

	const parseDataTableToGeoJSON = (
		dt: DataTable,
		latField: string,
		lonField: string,
		propsFields: Record<string, string>
	): FeatureCollection | undefined => {
		const propsFieldsList = Object.keys(propsFields);
		const allFields = [latField, lonField, ...propsFieldsList];
		const sourceFieldNames = dt.columns.map((c) => c.name);
		if (allFields.every((f) => sourceFieldNames.includes(f))) {
			const features = dt.rows.map((row, id) => {
				const coords = toValidCoords(row[lonField], row[latField]);
				if (!coords) return;
				const properties: Record<string, string | number | Date> = {};
				for (const field of propsFieldsList) {
					if (row[field] === '' || row[field] === null) return;
					properties[propsFields[field]] = row[field] as string | number | Date;
				}
				properties.id = id;
				return pointFeature(coords, properties);
			});
			return toCollection(features);
		} else {
			throw new Error(`One or more required fields (${allFields.join(', ')}) not found in data.`);
		}
	};

	async function tryLoadAndParse<T>(
		file: File,
		load: () => Promise<T>,
		parse: (data: T) => FeatureCollection | undefined
	): Promise<FeatureCollection | undefined> {
		let data: T;
		try {
			data = await load();
		} catch (error) {
			log.error('The following error was encountered reading {fileName}: {error}', {
				fileName: file.name,
				error
			});
			return;
		}
		try {
			return parse(data);
		} catch {
			log.error(
				'Error parsing {fileName}, possibly expected columns are changed or missing, or incorrect file was uploaded.',
				{ fileName: file.name }
			);
		}
	}

	const loadZipDbf = async (file: File): Promise<FeatureCollection | undefined> => {
		const dbfLoader = await import('@safesystem/zip-dbf-reader-lib');
		return tryLoadAndParse(
			file,
			() => dbfLoader.readDbfFromZip(file),
			(dt) =>
				parseDataTableToGeoJSON(dt, 'LatDec', 'Long', {
					DateofInci: 'Date of Incident',
					'KABCO Seve': 'Severity',
					Fatalities: 'Fatalities',
					'LRSP Manne': 'Manner of Collision',
					'Surface Co': 'Surface Condition',
					'Location a': 'Location Type'
				})
		);
	};

	interface XlsxData {
		rows: Record<string, string | number>[];
	}

	const mapXlsxKabco: Record<string, string> = {
		'Fatal (K)': '(K) Fatal Injury',
		'Evident, Incapacitating (A)': '(A) Suspected Serious Injury',
		'Evident Non-Incapacitating (B)': '(B) Suspected Minor/Visible Injury',
		'Possible/Complaint of Injury (C)': '(C) Possible Injury / Complaint'
	};

	const parseXlsxRowsToGeoJSON = (
		dt: XlsxData,
		latField: string,
		lonField: string
	): FeatureCollection | undefined => {
		const propsFieldsList = Object.keys(dt.rows[0]).filter((f) => f !== latField && f !== lonField);
		const features = dt.rows.map((row, id) => {
			const coords = toValidCoords(row[lonField], row[latField]);
			if (!coords) return;
			const properties: Record<string, string | number | Date> = {};
			for (const field of propsFieldsList) {
				if (row[field] === '' || row[field] === null) return;
				let value = row[field];
				if (field === 'Severity' && Object.hasOwn(mapXlsxKabco, value)) {
					value = mapXlsxKabco[value as string];
				}
				if (field === 'Date of Incident') {
					const [mm, dd, yyyy] = (value as string).split('/');
					value = `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T00:01:00.000`;
				}
				properties[field] = value;
			}
			properties.id = id;
			return pointFeature(coords, properties);
		});
		return toCollection(features);
	};

	const loadXlsx = async (file: File): Promise<FeatureCollection | undefined> => {
		const { readSheet } = await import('read-excel-file/browser');
		const schema = {
			latitude: { column: 'Latitude', type: Number },
			longitude: { column: 'Longitude', type: Number },
			'Date of Incident': { column: 'Date', type: String },
			Severity: { column: 'Severity', type: String },
			Fatalities: { column: 'Fatalities', type: Number },
			'Manner of Collision': { column: 'Crash Type', type: String },
			'Surface Condition': { column: 'Road Cond', type: String },
			'Location Type': { column: 'Road Description', type: String }
		} as const;
		return tryLoadAndParse(
			file,
			() => readSheet(file, { schema }),
			// `readSheet` with a schema returns `{ objects, errors }`; on schema
			// failure `objects` is undefined, which `parseXlsxRowsToGeoJSON` then
			// surfaces as a parse error via `tryLoadAndParse`.
			(result) => parseXlsxRowsToGeoJSON({ rows: result.objects ?? [] }, 'latitude', 'longitude')
		);
	};

	const parseCsv = async (file: File): Promise<unknown> => {
		const papa = await import('papaparse');
		return new Promise((resolve, reject) =>
			papa.parse(file, { header: true, skipEmptyLines: true, complete: resolve, error: reject })
		);
	};

	interface CsvData {
		data: Record<string, string | number>[];
	}

	const mapCsvKabco: Record<string, string> = {
		'01': '(K) Fatal Injury',
		'02': '(A) Suspected Serious Injury',
		'03': '(B) Suspected Minor/Visible Injury',
		'04': '(C) Possible Injury / Complaint'
	};
	const mapCsvLocation: Record<string, string> = {
		'01': 'Gore',
		'02': 'In Parking Lane or Zone',
		'03': 'Median',
		'04': 'Off-Roadway, Location Unknown',
		'05': 'On Roadway',
		'06': 'On Shoulder, Left Side',
		'07': 'On Shoulder, Right Side',
		'08': 'Outside Road/Right-of-Way',
		'09': 'Roadside',
		'10': 'Separator/Traffic Island',
		'99': 'Unknown'
	};
	const mapCsvManner: Record<string, string> = {
		'00': 'Not a Collision Between Two Motor Vehicles',
		'01': 'Angle',
		'02': 'Front to Front',
		'03': 'Front to Rear',
		'04': 'Rear to Rear',
		'05': 'Rear to Side',
		'06': 'Sideswipe, Opposite Direction',
		'07': 'Sideswipe, Same Direction',
		'98': 'Other',
		'99': 'Unknown'
	};
	const mapCsvSurfaceCondition: Record<string, string> = {
		'01': 'Dry',
		'07': 'Snow',
		'02': 'Ice/Frost',
		'08': 'Water (standing, moving)',
		'03': 'Mud, Dirt, Gravel',
		'09': 'Wet',
		'04': 'Oil',
		'05': 'Sand',
		'98': 'Other',
		'06': 'Slush',
		'99': 'Unknown'
	};

	const parseCsvRowsToGeoJSON = (
		dt: CsvData,
		latField: string,
		lonField: string
	): FeatureCollection | undefined => {
		const features = dt.data.map((row, id) => {
			const coords = toValidCoords(row[lonField], row[latField]);
			if (!coords) return;
			const date = `${row.C3_S1_YYYY}-${(row.C3_S1_MM as string).padStart(2, '0')}-${(row.C3_S1_DD as string).padStart(2, '0')}T00:01:00.000`;
			const fatalities = Number(row.C24);
			const lookup = (map: Record<string, string>, key: unknown) =>
				Object.hasOwn(map, key as string) ? map[key as string] : undefined;
			const kabco = lookup(mapCsvKabco, row.C19);
			const manner = lookup(mapCsvManner, row.C9);
			const location = lookup(mapCsvLocation, row.C8);
			const surfaceCondition = lookup(mapCsvSurfaceCondition, row.C13);

			if ([kabco, manner, location, surfaceCondition].includes(undefined)) return;

			const properties: Record<string, string | number> = {
				'Date of Incident': date,
				Severity: kabco || '',
				Fatalities: fatalities,
				'Manner of Collision': manner || '',
				'Surface Condition': surfaceCondition || '',
				'Location Type': location || ''
			};
			properties.id = id;
			return pointFeature(coords, properties);
		});
		return toCollection(features);
	};

	const loadCsv = (file: File): Promise<FeatureCollection | undefined> =>
		tryLoadAndParse(
			file,
			() => parseCsv(file),
			(data) => parseCsvRowsToGeoJSON(data as CsvData, 'C6_Lat', 'C6_Long')
		);

	// ---- Event handlers ----

	async function onFileChange(event: Event) {
		const target = event.target as HTMLInputElement;
		if (!target.files || target.files.length === 0) return;
		const file = target.files[0];

		if (file.size > MAX_FILE_BYTES) {
			log.error('File {fileName} is {fileSize} MB; uploads are limited to {maxSize} MB.', {
				fileName: file.name,
				fileSize: Math.round(file.size / 1024 / 1024),
				maxSize: MAX_FILE_BYTES / 1024 / 1024
			});
			customCrashesStore.showError = true;
			target.value = '';
			return;
		}

		customCrashesStore.showError = false;
		loading = true;
		// try/finally: a failed parser chunk import must not leave the spinner up.
		try {
			let crashes: FeatureCollection | undefined = undefined;

			if (file.name.endsWith('.zip')) {
				crashes = await loadZipDbf(file);
			} else if (file.name.endsWith('.xlsx')) {
				crashes = await loadXlsx(file);
			} else if (file.name.endsWith('.csv')) {
				crashes = await loadCsv(file);
			} else {
				log.error('Unrecognized file type. File name: {fileName}', { fileName: file.name });
				target.value = '';
			}

			if (crashes === undefined || crashes.features.length === 0) {
				customCrashesStore.showError = true;
			} else {
				customCrashesStore.data = crashes;
				setCrashLayer(true);
			}
		} catch (error) {
			log.error('Failed to load a parser for {fileName}: {error}', { fileName: file.name, error });
			customCrashesStore.showError = true;
		} finally {
			loading = false;
		}
	}

	function removeCustomCrashes() {
		customCrashesStore.data = undefined;
		featureSelectionStore.clearSelectedFeatures();
		setCrashLayer(false);
	}

	function setCrashLayer(enabled: boolean) {
		if (!crashLegend) return;
		legendStore.setLegendLayerVisibility({
			layerId: crashLegend.layerId,
			legendItemId: crashLegend.legendItems[0].legendItemId,
			enabled
		});
	}
</script>

{#if !customCrashesStore.data?.features.length && !loading}
	<label class="uploadLabel">
		<span class="screenReaderOnly">Upload crash data (.xlsx, shapefile .zip, .csv)</span>
		<input type="file" accept=".zip,.xlsx,.csv" onchange={onFileChange} />
	</label>
{:else if loading}
	<LoadingSpinner size="sm" inline />
{:else if crashLegend}
	<div class="legendRowBox">
		{#each crashLegend.legendItems as { color, label, legendItemId, enabled } (legendItemId)}
			<div class="rowBox">
				<LegendRow
					{label}
					checked={enabled}
					onchange={() =>
						legendStore.setLegendLayerVisibility({
							layerId: crashLegend.layerId,
							legendItemId,
							enabled: !enabled
						})}>
					<LegendPatch color={color ?? PATCH_FALLBACK} shape="round" />
				</LegendRow>
			</div>
		{/each}
	</div>
	<div class="crashCount">{customCrashesStore.data?.features.length ?? 0} crashes.</div>
	<AbleyButton variant="text" size="sm" onclick={removeCustomCrashes}>Remove</AbleyButton>
{/if}

<style>
	.uploadLabel input[type='file'] {
		cursor: pointer;
		max-width: 100%;
		font-size: var(--font-size-xs);
	}

	.legendRowBox {
		display: flex;
		flex-direction: column;
	}

	.rowBox {
		margin: 0 0 0.94rem;
	}

	.crashCount {
		margin-bottom: 0.2rem;
		font-size: var(--font-size-sm);
	}
</style>
